import {
  AlertTriangle,
  ArrowLeft,
  Bot,
  CalendarDays,
  Camera,
  Check,
  ChevronDown,
  Clipboard,
  Grape,
  MapPin,
  MoveRight,
  Plus,
  Save,
  Sparkles,
  Trash2,
  X,
  Wine as WineIcon,
} from "lucide-react";
import {
  type FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AppShell } from "../components/AppShell";
import { LocationSelect } from "../components/LocationSelect";
import { Modal } from "../components/Modal";
import { ErrorState, PageLoader } from "../components/States";
import { useToast } from "../components/Toast";
import { WinePhotoImportModal } from "../components/WinePhotoImportModal";
import { api } from "../lib/api";
import { copyText } from "../lib/clipboard";
import { todayInParis as today } from "../lib/date";
import {
  inventoryReturnPath,
  withInventoryReturn,
} from "../lib/inventory-navigation";
import { Link, useLocation, useNavigate, useParams } from "../lib/router";
import {
  bottleLabel,
  buildWineCompletionPrompt,
  currentYear as currentYearInParis,
  vintageLabel,
  wineLocationStocks,
} from "../lib/wine";
import type { WinePhotoAiValues } from "../lib/wine-photo-ai";
import { LocationEditorModal } from "./LocationsPage";
import {
  CATEGORIES,
  type CellarLocation,
  type CellarPlaceRecord,
  type CompletionField,
  type CreateWinePayload,
  type EntryFields,
  type InitialStockAllocation,
  type Wine,
  type WineCategory,
  type WineFields,
} from "../types";

const currentYear = currentYearInParis();
const NO_LOCATION_VALUE = "__sans_emplacement__";
const EMPTY_WINE: WineFields = {
  producteur: "",
  nom: "",
  appellation: "",
  region: "",
  cepages: "",
  vinification_elevage: "",
  millesime: currentYear,
  categorie: "Rouge",
  format: "75 cl",
  emplacement_id: null,
  debut_deg: null,
  fin_deg: null,
  note: "",
};

const EMPTY_ENTRY: EntryFields = {
  quantite: 1,
  date_effective: today(),
  prix_unitaire: null,
  vendeur: "",
  note: "",
};

interface Suggestions {
  producteur: string[];
  appellation: string[];
  region: string[];
}

function duplicateLocationSummary(wine: Wine): string {
  const stocks = wineLocationStocks(wine).filter((item) => item.stock > 0);
  if (stocks.length > 1) return `${stocks.length} emplacements`;
  return (
    stocks[0]?.emplacement ||
    stocks[0]?.emplacement_nom ||
    stocks[0]?.lieu ||
    "Emplacement non renseigné"
  );
}

export function WineFormPage() {
  const { id } = useParams();
  const { search } = useLocation();
  const editing = Boolean(id);
  const navigate = useNavigate();
  const returnToInventory = inventoryReturnPath(search);
  const backPath = editing && id
    ? withInventoryReturn(`/vins/${id}`, returnToInventory)
    : returnToInventory;
  const { showToast } = useToast();
  const [wine, setWine] = useState<WineFields>(EMPTY_WINE);
  const [entry, setEntry] = useState<EntryFields>(EMPTY_ENTRY);
  const [splitInitialStock, setSplitInitialStock] = useState(false);
  const [initialDistribution, setInitialDistribution] = useState<InitialStockAllocation[]>([]);
  const [nonVintage, setNonVintage] = useState(false);
  const [vintageInput, setVintageInput] = useState(String(currentYear));
  const [startInput, setStartInput] = useState(
    () => (editing ? "" : String(currentYearInParis())),
  );
  const [endInput, setEndInput] = useState("");
  const [priceInput, setPriceInput] = useState("");
  const [loading, setLoading] = useState(editing);
  const [loadError, setLoadError] = useState("");
  const [loadedModification, setLoadedModification] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [duplicates, setDuplicates] = useState<Wine[]>([]);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);
  const [allowDuplicate, setAllowDuplicate] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [suggestions, setSuggestions] = useState<Suggestions>({
    producteur: [],
    appellation: [],
    region: [],
  });
  const [locations, setLocations] = useState<CellarLocation[]>([]);
  const [places, setPlaces] = useState<CellarPlaceRecord[]>([]);
  const [locationsError, setLocationsError] = useState("");
  const [quickLocationOpen, setQuickLocationOpen] = useState(false);
  const [completionOpen, setCompletionOpen] = useState(false);
  const [photoImportOpen, setPhotoImportOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(true);
  const duplicateRef = useRef<HTMLDivElement>(null);

  const loadWine = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setLoadError("");
    try {
      const loaded = await api.getWine(id);
      setLoadedModification(loaded.date_modification ?? null);
      setWine({
        producteur: loaded.producteur,
        nom: loaded.nom,
        appellation: loaded.appellation ?? "",
        region: loaded.region ?? "",
        cepages: loaded.cepages ?? "",
        vinification_elevage: loaded.vinification_elevage ?? "",
        millesime: loaded.millesime,
        categorie: loaded.categorie,
        format: loaded.format,
        emplacement_id: loaded.emplacement_id,
        debut_deg: loaded.debut_deg,
        fin_deg: loaded.fin_deg,
        note: loaded.note ?? "",
      });
      setNonVintage(loaded.millesime == null);
      setVintageInput(loaded.millesime == null ? "" : String(loaded.millesime));
      setStartInput(loaded.debut_deg == null ? "" : String(loaded.debut_deg));
      setEndInput(loaded.fin_deg == null ? "" : String(loaded.fin_deg));
    } catch (caught) {
      setLoadError(caught instanceof Error ? caught.message : "Cette fiche est inaccessible.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void loadWine();
  }, [loadWine]);

  useEffect(() => {
    let active = true;
    void Promise.all([
      api.getSuggestions("producteur"),
      api.getSuggestions("appellation"),
      api.getSuggestions("region"),
    ])
      .then(([producteur, appellation, region]) => {
        if (active) setSuggestions({ producteur, appellation, region });
      })
      .catch(() => {
        // Les suggestions améliorent la saisie mais ne doivent jamais la bloquer.
      });
    return () => { active = false; };
  }, []);

  const loadLocations = useCallback(async () => {
    setLocationsError("");
    try {
      const [locationValues, placeValues] = await Promise.all([
        api.listLocations(),
        api.listPlaces(),
      ]);
      setLocations(locationValues);
      setPlaces(placeValues);
    } catch (caught) {
      setLocationsError(
        caught instanceof Error ? caught.message : "Les emplacements sont indisponibles.",
      );
    }
  }, []);

  useEffect(() => {
    void loadLocations();
  }, [loadLocations]);

  const parsedWine = useMemo<WineFields>(
    () => ({
      ...wine,
      format: wine.format.trim() || "75 cl",
      millesime: nonVintage || !vintageInput ? null : Number(vintageInput),
      debut_deg: startInput ? Number(startInput) : null,
      fin_deg: endInput ? Number(endInput) : null,
    }),
    [endInput, nonVintage, startInput, vintageInput, wine],
  );
  const initialQuantity = splitInitialStock
    ? initialDistribution.reduce((total, item) => total + item.quantite, 0)
    : entry.quantite;

  const enableInitialDistribution = () => {
    const firstLocation = wine.emplacement_id;
    const nextLocation = locations.find(
      (location) => location.id !== firstLocation,
    )?.id ?? null;
    const initialTotal = Math.max(1, entry.quantite);
    setInitialDistribution([
      {
        emplacement_id: firstLocation,
        quantite: initialTotal > 1 ? initialTotal - 1 : 1,
      },
      {
        emplacement_id: nextLocation,
        quantite: initialTotal > 1 ? 1 : 0,
      },
    ]);
    setSplitInitialStock(true);
  };

  const updateInitialAllocation = (
    index: number,
    values: Partial<InitialStockAllocation>,
  ) => {
    setInitialDistribution((current) =>
      current.map((item, itemIndex) =>
        itemIndex === index ? { ...item, ...values } : item,
      ),
    );
  };

  useEffect(() => {
    if (
      editing ||
      !parsedWine.producteur.trim() ||
      !parsedWine.nom.trim() ||
      (!nonVintage && !vintageInput) ||
      !parsedWine.categorie ||
      !parsedWine.format.trim()
    ) {
      setDuplicates([]);
      setCheckingDuplicate(false);
      return;
    }

    let active = true;
    const timer = window.setTimeout(() => {
      setCheckingDuplicate(true);
      void api
        .findDuplicates(parsedWine)
        .then((matches) => {
          if (active) {
            setDuplicates(matches);
            setAllowDuplicate(false);
          }
        })
        .catch(() => {
          if (active) setDuplicates([]);
        })
        .finally(() => {
          if (active) setCheckingDuplicate(false);
        });
    }, 450);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [editing, nonVintage, parsedWine, vintageInput]);

  const updateWine = <K extends keyof WineFields>(key: K, value: WineFields[K]) => {
    setWine((current) => ({ ...current, [key]: value }));
    if (!editing) setAllowDuplicate(false);
  };

  const applyPhotoValues = (values: WinePhotoAiValues) => {
    const hasVintage = Object.prototype.hasOwnProperty.call(values, "millesime");
    const { millesime, ...otherValues } = values;
    setWine((current) => {
      const next = { ...current, ...otherValues };
      if (hasVintage && (typeof millesime === "number" || millesime === null)) {
        next.millesime = millesime;
      }
      return next;
    });
    if (hasVintage && millesime === null) {
      setNonVintage(true);
      setVintageInput("");
    } else if (hasVintage && typeof millesime === "number") {
      setNonVintage(false);
      setVintageInput(String(millesime));
    }
    setAllowDuplicate(false);
    setFormError("");
    setMoreOpen(true);
    setPhotoImportOpen(false);
    const count = Object.keys(values).length;
    showToast({
      message: `${count} information${count > 1 ? "s" : ""} préremplie${count > 1 ? "s" : ""}. Vérifiez la fiche avant de l’ajouter.`,
    });
  };

  const validate = (): string => {
    if (!parsedWine.producteur.trim()) return "Le producteur ou domaine est obligatoire.";
    if (!parsedWine.nom.trim()) return "Le nom du vin est obligatoire.";
    if (!nonVintage) {
      const year = Number(vintageInput);
      if (!Number.isInteger(year) || year < 1000 || year > 9999) {
        return "Indiquez un millésime valide ou choisissez Non millésimé.";
      }
    }
    if (!editing && (!Number.isInteger(initialQuantity) || initialQuantity < 1)) {
      return "La quantité initiale doit être un entier supérieur ou égal à 1.";
    }
    if (!editing && splitInitialStock) {
      if (
        initialDistribution.length < 2 ||
        initialDistribution.some(
          (item) => !Number.isInteger(item.quantite) || item.quantite < 1,
        )
      ) {
        return "Chaque emplacement doit recevoir au moins une bouteille.";
      }
      const locationKeys = initialDistribution.map((item) =>
        item.emplacement_id ?? NO_LOCATION_VALUE,
      );
      if (new Set(locationKeys).size !== locationKeys.length) {
        return "Chaque emplacement ne peut apparaître qu’une fois dans la répartition.";
      }
    }
    if (parsedWine.debut_deg != null && (!Number.isInteger(parsedWine.debut_deg) || parsedWine.debut_deg < 1000 || parsedWine.debut_deg > 9999)) {
      return "L’année de début de dégustation n’est pas valide.";
    }
    if (parsedWine.fin_deg != null && (!Number.isInteger(parsedWine.fin_deg) || parsedWine.fin_deg < 1000 || parsedWine.fin_deg > 9999)) {
      return "L’année de fin de dégustation n’est pas valide.";
    }
    if (
      parsedWine.debut_deg != null &&
      parsedWine.fin_deg != null &&
      parsedWine.debut_deg > parsedWine.fin_deg
    ) {
      return "Le début de dégustation doit précéder ou égaler la fin.";
    }
    if (priceInput) {
      const price = Number(priceInput.replace(",", "."));
      if (!Number.isFinite(price) || price < 0) return "Le prix doit être un montant positif.";
    }
    return "";
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const validationError = validate();
    if (validationError) {
      setFormError(validationError);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    if (!editing && duplicates.length && !allowDuplicate) {
      setFormError("Une référence très proche existe déjà. Choisissez ci-dessous comment continuer.");
      duplicateRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    setSaving(true);
    setFormError("");
    try {
      if (editing && id) {
        const updated = loadedModification
          ? await api.updateWine(id, parsedWine, loadedModification)
          : await api.updateWine(id, parsedWine);
        showToast({ message: "La fiche a été mise à jour." });
        navigate(withInventoryReturn(`/vins/${updated.id || id}`, returnToInventory));
      } else {
        const payload: CreateWinePayload = {
          ...parsedWine,
          emplacement_id: splitInitialStock
            ? initialDistribution[0]?.emplacement_id ?? null
            : parsedWine.emplacement_id,
          entree: {
            ...entry,
            quantite: initialQuantity,
            prix_unitaire: priceInput ? Number(priceInput.replace(",", ".")) : null,
          },
          ...(splitInitialStock
            ? { repartition_initiale: initialDistribution }
            : {}),
        };
        const created = await api.createWine(payload);
        showToast({ message: `${initialQuantity} bouteille${initialQuantity === 1 ? "" : "s"} ajoutée${initialQuantity === 1 ? "" : "s"} à votre cave.` });
        navigate(
          created.id
            ? withInventoryReturn(`/vins/${created.id}`, returnToInventory)
            : returnToInventory,
        );
      }
    } catch (caught) {
      setFormError(caught instanceof Error ? caught.message : "L’enregistrement a échoué.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setSaving(false);
    }
  };

  const addToExisting = async (existing: Wine) => {
    const validationError = validate();
    if (validationError) { setFormError(validationError); return; }
    setSaving(true); setFormError("");
    try {
      const allocations = splitInitialStock
        ? initialDistribution
        : [{ emplacement_id: parsedWine.emplacement_id, quantite: entry.quantite }];
      await api.createMovement(existing.id, {
        quantite: initialQuantity,
        motif: "Achat",
        date_effective: entry.date_effective || null,
        prix_unitaire: priceInput ? Number(priceInput.replace(",", ".")) : null,
        vendeur: entry.vendeur.trim(),
        note: entry.note.trim(),
        emplacement_id: allocations[0]?.emplacement_id ?? null,
        ...(splitInitialStock ? { repartition: allocations } : {}),
      });
      showToast({ message: `${initialQuantity === 1 ? "La bouteille a" : "Les bouteilles ont"} été ajoutée${initialQuantity === 1 ? "" : "s"} à « ${existing.nom} ».` });
      navigate(withInventoryReturn(`/vins/${existing.id}`, returnToInventory));
    } catch (caught) {
      setFormError(caught instanceof Error ? caught.message : "Impossible d’ajouter ces bouteilles.");
    } finally { setSaving(false); }
  };

  const deleteWine = async () => {
    if (!id) return;
    setDeleting(true);
    try {
      if (loadedModification) await api.deleteWine(id, loadedModification);
      else await api.deleteWine(id);
      showToast({ message: "La fiche et son historique ont été supprimés.", tone: "info" });
      navigate(returnToInventory);
    } catch (caught) {
      setFormError(caught instanceof Error ? caught.message : "Suppression impossible.");
      setDeleteOpen(false);
    } finally { setDeleting(false); }
  };

  if (loading) return <AppShell><div className="page"><PageLoader label="Ouverture du formulaire…" /></div></AppShell>;
  if (loadError) return <AppShell><div className="page narrow-page"><Link className="back-link" to={backPath}><ArrowLeft size={17} /> Retour</Link><ErrorState title="Impossible de modifier cette fiche" message={loadError} onRetry={() => void loadWine()} /></div></AppShell>;

  return (
    <AppShell>
      <div className="page form-page">
        <Link className="back-link" to={backPath}><ArrowLeft size={17} /> {editing ? "Retour à la fiche" : "Retour à l’inventaire"}</Link>
        <header className="form-header">
          <div><p className="eyebrow">{editing ? "Mise à jour" : "Nouvelle référence"}</p><h1>{editing ? "Modifier la fiche" : "Ajouter un vin"}</h1><p>{editing ? "Les changements concernent la fiche uniquement, jamais le stock ni l’historique." : "Les informations essentielles d’abord. Vous pourrez compléter le reste plus tard."}</p></div>
          <span className="form-header-icon" aria-hidden="true"><Grape size={27} /></span>
        </header>

        {formError ? <div className="form-alert" role="alert"><AlertTriangle size={20} /><p>{formError}</p></div> : null}

        <form className="wine-form" onSubmit={submit} noValidate>
          {!editing ? (
            <section className="wine-photo-entry-card">
              <span className="wine-photo-entry-icon" aria-hidden="true"><Camera size={24} /></span>
              <div>
                <p className="eyebrow">Saisie assistée</p>
                <h2>Ajouter depuis une photo</h2>
                <p>Prenez les étiquettes en photo ou choisissez des images déjà prises, puis vérifiez les informations proposées par Gemini.</p>
              </div>
              <button className="button button-primary" type="button" onClick={() => setPhotoImportOpen(true)}>
                <Camera size={18} aria-hidden="true" /> Ajouter depuis une photo
              </button>
            </section>
          ) : null}
          <section className="form-section">
            <div className="form-section-heading"><span>1</span><div><h2>L’essentiel</h2><p>Tout ce qu’il faut pour reconnaître et retrouver la bouteille.</p></div></div>
            <div className="form-grid two-columns">
              <label className="field"><span>Appellation <em>facultative</em></span><input list="appellation-suggestions" value={wine.appellation} onChange={(event) => updateWine("appellation", event.target.value)} placeholder="Ex. Bandol" /><datalist id="appellation-suggestions">{suggestions.appellation.map((item) => <option key={item} value={item} />)}</datalist></label>
              <label className="field"><span>Région / pays <em>facultatif</em></span><input list="region-suggestions" value={wine.region} onChange={(event) => updateWine("region", event.target.value)} placeholder="Ex. Provence, France" /><datalist id="region-suggestions">{suggestions.region.map((item) => <option key={item} value={item} />)}</datalist></label>
              <label className="field"><span>Producteur / domaine <b>*</b></span><input list="producer-suggestions" autoComplete="off" required value={wine.producteur} onChange={(event) => updateWine("producteur", event.target.value)} placeholder="Ex. Domaine Tempier" /><datalist id="producer-suggestions">{suggestions.producteur.map((item) => <option key={item} value={item} />)}</datalist></label>
              <label className="field"><span>Nom du vin / cuvée <b>*</b></span><input required value={wine.nom} onChange={(event) => updateWine("nom", event.target.value)} placeholder="Ex. Cuvée Classique" /></label>
              <fieldset className="field vintage-field"><legend>Millésime <b>*</b></legend><input type="number" min="1000" max="9999" inputMode="numeric" disabled={nonVintage} required={!nonVintage} value={vintageInput} onChange={(event) => { setVintageInput(event.target.value); setAllowDuplicate(false); }} /><label className="check-row"><input type="checkbox" checked={nonVintage} onChange={(event) => { setNonVintage(event.target.checked); if (event.target.checked) setVintageInput(""); else setVintageInput(String(currentYear)); setAllowDuplicate(false); }} /><span><Check size={14} /> Non millésimé</span></label></fieldset>
              <label className="field category-field">
                <span>Catégorie <b>*</b></span>
                <select
                  required
                  value={wine.categorie}
                  onChange={(event) => updateWine("categorie", event.target.value as WineCategory)}
                >
                  {!(CATEGORIES as readonly string[]).includes(wine.categorie) ? (
                    <option value={wine.categorie}>{wine.categorie} — à reclasser</option>
                  ) : null}
                  {CATEGORIES.map((item) => <option key={item}>{item}</option>)}
                </select>
                {wine.categorie === "Liquoreux" ? (
                  <small className="category-help">Sauternes, Monbazillac, Tokaji, vendanges tardives…</small>
                ) : null}
              </label>
              {!editing && !splitInitialStock ? <label className="field quantity-field"><span>Quantité initiale <b>*</b></span><div className="quantity-control"><button type="button" aria-label="Retirer une bouteille" onClick={() => setEntry((current) => ({ ...current, quantite: Math.max(1, current.quantite - 1) }))}>−</button><input aria-label="Quantité initiale" type="number" min="1" step="1" inputMode="numeric" required value={entry.quantite} onChange={(event) => setEntry((current) => ({ ...current, quantite: Number(event.target.value) }))} /><button type="button" aria-label="Ajouter une bouteille" onClick={() => setEntry((current) => ({ ...current, quantite: current.quantite + 1 }))}>+</button></div></label> : null}
              {!editing && !splitInitialStock ? (
                <div className="field location-form-field">
                  <span>Emplacement <em>facultatif</em></span>
                  <div className="location-picker-row">
                    <div className="input-with-icon"><MapPin size={17} aria-hidden="true" /><LocationSelect locations={locations} places={places} value={wine.emplacement_id ?? ""} onChange={(value) => updateWine("emplacement_id", value || null)} emptyLabel="Sans emplacement" /></div>
                    <button className="button button-secondary quick-location-button" type="button" onClick={() => setQuickLocationOpen(true)}><Plus size={16} aria-hidden="true" /> Nouvel emplacement</button>
                  </div>
                  <button className="split-stock-trigger" type="button" onClick={enableInitialDistribution}><MoveRight size={16} aria-hidden="true" /> Répartir sur plusieurs emplacements</button>
                  {locationsError ? <p className="field-inline-error" role="alert">{locationsError} <button type="button" onClick={() => void loadLocations()}>Réessayer</button></p> : null}
                </div>
              ) : null}
              {!editing && splitInitialStock ? (
                <fieldset className="initial-distribution">
                  <legend>Répartition du stock initial</legend>
                  <p>Indiquez combien de bouteilles seront rangées à chaque emplacement.</p>
                  <div className="initial-distribution-rows">
                    {initialDistribution.map((allocation, index) => (
                      <div className="initial-distribution-row" key={index}>
                        <label className="field">
                          <span>Emplacement {index + 1}</span>
                          <LocationSelect locations={locations} places={places} value={allocation.emplacement_id ?? ""} onChange={(value) => updateInitialAllocation(index, { emplacement_id: value || null })} emptyLabel="Sans emplacement" />
                        </label>
                        <label className="field distribution-quantity">
                          <span>Quantité</span>
                          <input type="number" min="1" step="1" inputMode="numeric" required value={allocation.quantite} onChange={(event) => updateInitialAllocation(index, { quantite: Number(event.target.value) })} />
                        </label>
                        {initialDistribution.length > 2 ? <button className="distribution-remove" type="button" aria-label={`Retirer l’emplacement ${index + 1}`} onClick={() => setInitialDistribution((current) => current.filter((_, itemIndex) => itemIndex !== index))}><X size={17} aria-hidden="true" /></button> : null}
                      </div>
                    ))}
                  </div>
                  <div className="distribution-footer">
                    <button className="button button-secondary" type="button" onClick={() => setInitialDistribution((current) => [...current, { emplacement_id: locations.find((location) => !current.some((item) => item.emplacement_id === location.id))?.id ?? null, quantite: 1 }])}><Plus size={16} aria-hidden="true" /> Ajouter un emplacement</button>
                    <span><strong>{initialQuantity}</strong> bouteille{initialQuantity === 1 ? "" : "s"} au total</span>
                  </div>
                  <div className="distribution-secondary-actions">
                    <button type="button" onClick={() => setQuickLocationOpen(true)}>Créer un nouvel emplacement</button>
                    <button type="button" onClick={() => { setSplitInitialStock(false); setInitialDistribution([]); }}>Revenir à un seul emplacement</button>
                  </div>
                  {locationsError ? <p className="field-inline-error" role="alert">{locationsError} <button type="button" onClick={() => void loadLocations()}>Réessayer</button></p> : null}
                </fieldset>
              ) : null}
              {editing ? (
                <div className="edit-stock-location-note"><MapPin size={18} aria-hidden="true" /><p><strong>Les emplacements ne se modifient plus ici.</strong> Utilisez « Déplacer des bouteilles » sur la fiche pour conserver un historique exact.</p></div>
              ) : null}
              <div className="field period-fields" role="group" aria-labelledby="tasting-period-label"><span id="tasting-period-label">Période de dégustation <em>facultative</em></span><div><label><span className="sr-only">Début</span><input type="number" min="1000" max="9999" inputMode="numeric" value={startInput} onChange={(event) => setStartInput(event.target.value)} placeholder="Début" /></label><span aria-hidden="true">à</span><label><span className="sr-only">Fin</span><input type="number" min="1000" max="9999" inputMode="numeric" value={endInput} onChange={(event) => setEndInput(event.target.value)} placeholder="Fin" /></label></div></div>
            </div>
          </section>

          {!editing && (checkingDuplicate || duplicates.length > 0) ? (
            <div className="duplicate-alert" ref={duplicateRef}>
              <div className="duplicate-heading"><AlertTriangle size={21} /><div><h2>{checkingDuplicate ? "Vérification en cours…" : `${duplicates.length} référence${duplicates.length > 1 ? "s" : ""} semblable${duplicates.length > 1 ? "s" : ""} trouvée${duplicates.length > 1 ? "s" : ""}`}</h2><p>Une même référence peut désormais être répartie sur plusieurs emplacements.</p></div></div>
              {duplicates.map((duplicate) => (
                <div className="duplicate-card" key={duplicate.id}>
                  <div><strong>{duplicate.nom} — {duplicate.producteur}, {vintageLabel(duplicate)}</strong><span><MapPin size={14} /> {duplicateLocationSummary(duplicate)} · {bottleLabel(duplicate.stock)}</span></div>
                  <button className="button button-secondary" type="button" disabled={saving} onClick={() => void addToExisting(duplicate)}>Ajouter à cette référence</button>
                </div>
              ))}
              {duplicates.length ? <button className={`create-anyway ${allowDuplicate ? "selected" : ""}`} type="button" onClick={() => setAllowDuplicate(true)}><span className="radio-dot">{allowDuplicate ? <Check size={13} /> : null}</span><span><strong>Créer quand même une nouvelle référence</strong><small>À réserver à une bouteille réellement différente.</small></span></button> : null}
            </div>
          ) : null}

          <details className="more-section" open={moreOpen} onToggle={(event) => setMoreOpen(event.currentTarget.open)}>
            <summary><span><span className="summary-number">2</span><span><strong>Plus d’informations</strong><small>Cépages, format, vinification et notes</small></span></span><ChevronDown size={20} aria-hidden="true" /></summary>
            <div className="more-content">
              <div className="form-grid two-columns">
                <label className="field"><span>Cépage(s) <em>facultatif</em></span><input maxLength={500} value={wine.cepages} onChange={(event) => updateWine("cepages", event.target.value)} placeholder="Ex. 70 % Syrah, 30 % Grenache" /></label>
                <label className="field"><span>Format <em>facultatif</em></span><input list="format-suggestions" value={wine.format} onChange={(event) => updateWine("format", event.target.value)} placeholder="75 cl" /><datalist id="format-suggestions"><option value="37,5 cl" /><option value="50 cl" /><option value="75 cl" /><option value="1 L" /><option value="1,5 L" /><option value="3 L" /><option value="6 L" /></datalist></label>
              </div>
              <label className="field"><span>Vinification et élevage <em>facultatif</em></span><textarea rows={4} maxLength={10_000} value={wine.vinification_elevage} onChange={(event) => updateWine("vinification_elevage", event.target.value)} placeholder="Ex. vendanges manuelles, fermentation en cuve, élevage 12 mois en fûts…" /></label>
              <div className="ai-completion-card">
                <div><span className="ai-completion-icon"><Bot size={19} aria-hidden="true" /></span><div><h3>Besoin d’un coup de pouce ?</h3><p>Préparez un prompt à copier dans une IA en ligne pour rechercher les informations manquantes avec leurs sources.</p></div></div>
                <button className="button button-secondary" type="button" onClick={() => setCompletionOpen(true)} disabled={!parsedWine.producteur.trim() || !parsedWine.nom.trim()}><Sparkles size={17} aria-hidden="true" /> Compléter avec l’IA</button>
                {!parsedWine.producteur.trim() || !parsedWine.nom.trim() ? <small>Renseignez d’abord le producteur et le nom du vin.</small> : null}
              </div>
              <label className="field"><span>Note sur le vin <em>facultative</em></span><textarea rows={3} value={wine.note} onChange={(event) => updateWine("note", event.target.value)} placeholder="Un repère personnel sur cette cuvée…" /></label>

              {!editing ? (
                <div className="entry-fields">
                  <div className="subsection-heading"><CalendarDays size={18} /><div><h3>À propos de cette entrée</h3><p>Ces informations resteront attachées au mouvement de stock initial.</p></div></div>
                  <div className="form-grid two-columns">
                    <label className="field"><span>Date de cette entrée <em>facultative</em></span><input type="date" value={entry.date_effective} onChange={(event) => setEntry((current) => ({ ...current, date_effective: event.target.value }))} /></label>
                    <label className="field"><span>Prix par bouteille <em>facultatif</em></span><div className="input-with-suffix"><input type="text" inputMode="decimal" value={priceInput} onChange={(event) => setPriceInput(event.target.value)} placeholder="0,00" /><span>€</span></div></label>
                    <label className="field"><span>Vendeur / provenance <em>facultatif</em></span><input value={entry.vendeur} onChange={(event) => setEntry((current) => ({ ...current, vendeur: event.target.value }))} placeholder="Caviste, propriété…" /></label>
                    <label className="field"><span>Note sur cette entrée <em>facultative</em></span><input value={entry.note} onChange={(event) => setEntry((current) => ({ ...current, note: event.target.value }))} placeholder="Coffret, cadeau…" /></label>
                  </div>
                </div>
              ) : null}
            </div>
          </details>

          <div className="form-submit-bar">
            <p>{editing ? "Le stock et l’historique resteront inchangés." : <><WineIcon size={17} /> Un mouvement « Stock initial » sera créé.</>}</p>
            <div>
              <Link className="button button-ghost" to={backPath}>Annuler</Link>
              <button className="button button-primary" type="submit" disabled={saving || checkingDuplicate}><Save size={18} /> {saving ? "Enregistrement…" : editing ? "Enregistrer les modifications" : "Ajouter à ma cave"}</button>
            </div>
          </div>
        </form>

        {editing ? (
          <section className="danger-zone"><div><h2>Supprimer cette référence</h2><p>Cette action efface définitivement la fiche et tout son historique.</p></div><button className="button button-danger" type="button" onClick={() => setDeleteOpen(true)}><Trash2 size={17} /> Supprimer</button></section>
        ) : null}
      </div>

      <WinePhotoImportModal
        open={photoImportOpen}
        onClose={() => setPhotoImportOpen(false)}
        onApply={applyPhotoValues}
      />
      <Modal open={deleteOpen} onClose={() => setDeleteOpen(false)} title="Supprimer définitivement ?" description="Cette action est irréversible." size="small">
        <div className="delete-warning"><AlertTriangle size={22} /><p>La fiche <strong>{wine.nom || "de ce vin"}</strong> et <strong>tous ses mouvements d’entrée, de consommation et de correction</strong> seront supprimés.</p></div>
        <div className="modal-actions"><button className="button button-ghost" type="button" onClick={() => setDeleteOpen(false)}>Conserver la fiche</button><button className="button button-danger" type="button" disabled={deleting} onClick={() => void deleteWine()}><Trash2 size={17} /> {deleting ? "Suppression…" : "Oui, tout supprimer"}</button></div>
      </Modal>
      <LocationEditorModal
        open={quickLocationOpen}
        location={null}
        places={places}
        defaultPlaceId={
          locations.find(
            (location) =>
              location.id ===
              (splitInitialStock
                ? initialDistribution.at(-1)?.emplacement_id
                : wine.emplacement_id),
          )?.lieu_id ??
          places[0]?.id ??
          ""
        }
        onClose={() => setQuickLocationOpen(false)}
        onSaved={async (created) => {
          setLocations((current) =>
            [...current.filter((location) => location.id !== created.id), created].sort((a, b) =>
              a.libelle.localeCompare(b.libelle, "fr", { sensitivity: "base" }),
            ),
          );
          if (splitInitialStock) {
            setInitialDistribution((current) => [
              ...current,
              { emplacement_id: created.id, quantite: 1 },
            ]);
          } else {
            updateWine("emplacement_id", created.id);
          }
          setQuickLocationOpen(false);
          showToast({ message: `« ${created.libelle} » a été créé et sélectionné.` });
        }}
      />
      <CompletionAiModal
        open={completionOpen}
        wine={parsedWine}
        onClose={() => setCompletionOpen(false)}
      />
    </AppShell>
  );
}

function CompletionAiModal({
  open,
  wine,
  onClose,
}: {
  open: boolean;
  wine: WineFields;
  onClose: () => void;
}) {
  const [selected, setSelected] = useState<CompletionField[]>([]);
  const [prompt, setPrompt] = useState("");
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    const missing: CompletionField[] = [];
    if (!wine.appellation.trim()) missing.push("appellation");
    if (!wine.region.trim()) missing.push("region");
    if (!wine.cepages.trim()) missing.push("cepages");
    if (wine.debut_deg == null || wine.fin_deg == null) missing.push("periode");
    if (!wine.vinification_elevage.trim()) missing.push("vinification_elevage");
    setSelected(missing);
    setPrompt("");
    setCopied(false);
    setError("");
  }, [
    open,
    wine.appellation,
    wine.cepages,
    wine.debut_deg,
    wine.fin_deg,
    wine.region,
    wine.vinification_elevage,
  ]);

  const toggle = (field: CompletionField) => {
    setSelected((current) =>
      current.includes(field)
        ? current.filter((item) => item !== field)
        : [...current, field],
    );
    setError("");
  };

  const generate = () => {
    if (!selected.length) {
      setError("Sélectionnez au moins une information à rechercher.");
      return;
    }
    try {
      setPrompt(buildWineCompletionPrompt(wine, selected));
      setError("");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Le prompt n’a pas pu être généré.");
    }
  };

  const copy = async () => {
    try {
      await copyText(prompt);
      setCopied(true);
      setError("");
      window.setTimeout(() => setCopied(false), 2500);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "La copie a échoué.");
    }
  };

  const vinificationPreview = wine.vinification_elevage
    .trim()
    .replace(/\s+/gu, " ");
  const choices: Array<{
    field: CompletionField;
    title: string;
    help: string;
    current?: string;
  }> = [
    {
      field: "appellation",
      title: "Appellation",
      help: "Identifier l’appellation exacte.",
      current: wine.appellation.trim() || undefined,
    },
    {
      field: "region",
      title: "Région / pays",
      help: "Préciser l’origine géographique.",
      current: wine.region.trim() || undefined,
    },
    {
      field: "cepages",
      title: "Cépage(s)",
      help: "Identifier les cépages et leur proportion lorsqu’elle est connue.",
      current: wine.cepages.trim() || undefined,
    },
    {
      field: "periode",
      title: "Période de dégustation",
      help: "Obtenir une année de début et une année de fin.",
      current:
        wine.debut_deg != null || wine.fin_deg != null
          ? `${wine.debut_deg ?? "?"} – ${wine.fin_deg ?? "?"}`
          : undefined,
    },
    {
      field: "prix",
      title: "Prix",
      help: "Rechercher un prix actuel par bouteille.",
    },
    {
      field: "vinification_elevage",
      title: "Vinification et élevage",
      help: "Rechercher des informations factuelles à valider.",
      current: vinificationPreview
        ? `${vinificationPreview.slice(0, 160)}${vinificationPreview.length > 160 ? "…" : ""}`
        : undefined,
    },
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Compléter avec l’IA"
      description="L’application prépare seulement un prompt : rien n’est envoyé et aucun champ ne sera rempli automatiquement."
      size="large"
    >
      {!prompt ? (
        <div>
          <div className="privacy-note"><Bot size={20} aria-hidden="true" /><p><strong>Vous gardez la main.</strong> Vérifiez la réponse de l’IA puis reportez vous-même les informations que vous jugez fiables.</p></div>
          <fieldset className="completion-choices">
            <legend>Que souhaitez-vous rechercher ou vérifier ? <b>*</b></legend>
            {choices.map((choice) => (
              <label key={choice.field}>
                <input type="checkbox" checked={selected.includes(choice.field)} onChange={() => toggle(choice.field)} />
                <span className="completion-check"><Check size={14} aria-hidden="true" /></span>
                <span><strong>{choice.title}</strong><small>{choice.help}{choice.current ? ` Valeur actuelle : ${choice.current}.` : " Non renseigné."}</small></span>
              </label>
            ))}
          </fieldset>
          {error ? <p className="form-error" role="alert">{error}</p> : null}
          <div className="modal-actions">
            <button className="button button-ghost" type="button" onClick={onClose}>Fermer</button>
            <button className="button button-primary" type="button" disabled={!selected.length} onClick={generate}>
              <Sparkles size={18} aria-hidden="true" /> Générer le prompt
            </button>
          </div>
        </div>
      ) : (
        <div>
          <div className="prompt-heading"><div><span className="success-dot"><Check size={14} aria-hidden="true" /></span><strong>Prompt prêt à copier</strong></div><button type="button" onClick={() => { setPrompt(""); setCopied(false); }}>Modifier la sélection</button></div>
          <pre className="prompt-preview">{prompt}</pre>
          <p className="manual-entry-reminder">La réponse ne modifiera jamais cette fiche automatiquement.</p>
          {copied ? <p className="copy-confirmation" role="status"><Check size={16} aria-hidden="true" /> Prompt copié dans le presse-papiers.</p> : null}
          {error ? <p className="form-error" role="alert">{error}</p> : null}
          <div className="modal-actions"><button className="button button-ghost" type="button" onClick={onClose}>Fermer</button><button className="button button-primary" type="button" onClick={() => void copy()}><Clipboard size={18} aria-hidden="true" /> {copied ? "Copié" : "Copier le prompt"}</button></div>
        </div>
      )}
    </Modal>
  );
}
